import { Seo, buildArticleSchema } from '../../core/seo';

const advertisingArticleSchema = buildArticleSchema({
  headline: 'Advertising Policy',
  description:
    'How sponsored listings and advertisements work on HirePanther — including placement, labelling, and our commitment to fairness.',
  url: 'https://uat.hirepanther.ca/advertising-policy',
  datePublished: '2025-12-01',
  dateModified: '2025-12-01',
  publisher: {
    '@type': 'Organization',
    name: 'HirePanther',
    url: 'https://uat.hirepanther.ca',
  },
});

const AdvertisingPolicy = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Advertising Policy"
        description="How sponsored listings and advertisements work on HirePanther — including placement, labelling, and our commitment to fairness."
        canonicalPath="/advertising-policy"
        structuredData={advertisingArticleSchema}
      />
      <div className="min-h-screen bg-white text-[#284F60]  px-6 py-12 md:px-16 lg:px-32">
        {/* Page Header */}
        <header className="text-left mb-12">
          <h1 className="text-4xl font-bold text-[#284F60] mb-2">
            Advertising Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3">
            Effective Date: December 01, 2025 | Last Updated: December 01, 2025
          </p>
        </header>

        {/* Intro */}
        <section className="mb-10">
          <p className="text-lg leading-relaxed">
            This Advertising Policy explains how HirePanther ("we," "our," or
            "us") handles paid placements, sponsored listings, and other
            advertising on our Platform. We are committed to transparency so you
            always know when content is promoted.
          </p>
        </section>

        {/* Section 1: What Does Sponsored Mean? */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            1. What Does "Sponsored" Mean?
          </h2>
          <p className="mb-4">
            A "Sponsored" listing is one where a provider paid HirePanther to
            place their listing above regular search results for a fixed period.
            It is an advertisement.
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              Sponsorship never changes a listing's rating, reviews, or price.
            </li>
            <li>Sponsored listings are labelled everywhere they appear.</li>
            <li>
              All providers must pass the same ID verification and insurance
              checks.
            </li>
            <li>Paying does not affect dispute handling or refunds.</li>
          </ul>
        </section>

        {/* Section 2: How Placement Is Decided */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            2. How Placement Is Decided
          </h2>
          <p className="mb-4">
            Sponsored slots are limited to four per results page and ordered by
            boost package, then purchase time.
          </p>
          <p>
            Everything below the Sponsored group is ranked only by relevance,
            rating, and distance. Sponsorship does not influence organic search
            rankings.
          </p>
        </section>

        {/* Section 3: Types of Advertising */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            3. Types of Advertising on HirePanther
          </h2>

          <h3 className="text-xl font-medium text-[#2D617A] mt-4">
            a) Sponsored Listings
          </h3>
          <p className="mt-2 ml-4">
            Providers can purchase sponsored placements that appear at the top
            of relevant search results. These listings are clearly marked with a
            "Sponsored" badge.
          </p>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            b) Display Advertisements
          </h3>
          <p className="mt-2 ml-4">
            From time to time, we may show display ads or promotional banners on
            the Platform. These are always labelled as advertisements and are
            separate from search results.
          </p>
        </section>

        {/* Section 4: Labelling and Transparency */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            4. Labelling and Transparency
          </h2>
          <p className="mb-4">
            We label all paid placements with a visible "Sponsored" or
            "Advertisement" badge. You can always identify promoted content
            before clicking on it. Our commitment to transparency includes:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Every sponsored listing carries a distinct visual badge.</li>
            <li>
              Sponsored placements are separated from organic results with a
              clear header.
            </li>
            <li>
              Clicking the "Sponsored" badge shows an explanation of what
              sponsorship means.
            </li>
          </ul>
        </section>

        {/* Section 5: Provider Standards */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            5. Provider Standards for Advertisers
          </h2>
          <p className="mb-4">
            All providers who purchase sponsored placements must meet the same
            quality and verification standards as any other provider on our
            Platform. This includes:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Government-issued ID verification.</li>
            <li>Valid insurance coverage (where applicable).</li>
            <li>Compliance with our Terms of Service and Quality Standards.</li>
            <li>A minimum rating threshold to qualify for sponsorship.</li>
          </ul>
          <p className="mt-4">
            We reserve the right to reject or remove any sponsored listing that
            violates our policies.
          </p>
        </section>

        {/* Section 6: Your Choices */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            6. Your Choices
          </h2>
          <p className="mb-4">
            You can distinguish sponsored content from organic results at all
            times. If you prefer not to see interest-based advertising, you can:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              Adjust your browser settings to manage cookies and trackers.
            </li>
            <li>
              Opt out of targeted advertising through the Digital Advertising
              Alliance of Canada.
            </li>
            <li>
              Review our Cookie Policy for information on advertising cookies.
            </li>
          </ul>
        </section>

        {/* Section 7: Reporting Concerns */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            7. Reporting Concerns
          </h2>
          <p>
            If you believe a sponsored listing is misleading, inaccurate, or
            violates our policies, please report it through our Help Center or
            contact us at the address below.
          </p>
        </section>

        {/* Section 8: Updates */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            8. Updates to This Policy
          </h2>
          <p>
            We may update this Advertising Policy from time to time. Updates
            will be posted at /advertising-policy with a new effective date.
          </p>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            9. Contact Us
          </h2>
          <p>If you have questions about our advertising practices:</p>
          <p className="mt-2">
            <a
              href="mailto:privacy@hirepanther.com"
              className="text-blue-500 hover:underline"
            >
              privacy@hirepanther.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default AdvertisingPolicy;
